import { useState, type ReactNode } from 'react'
import {
  DEFAULT_LESSON_COLOR,
  coloringTag,
  groszToZloty,
  lessonColor,
  zlotyToGrosz,
  type Client,
  type ClientInput,
  type TagColors,
} from '@diana/shared'
import { ColorDot, ColorPicker } from '../../components/ColorPicker.js'
import { Button, ErrorLine } from '../../classroom/ui.js'

/**
 * Add / edit a client. Prices are typed in złoty and stored in grosze —
 * Diana should never see "12000".
 *
 * Drawn in the classroom style so it sits on the paper panel of both the
 * Uczniowie list and the client card: 12px labels, `cl-input` fields, one
 * soft-gold button.
 */
export function ClientForm({
  initial,
  tagColors,
  onSubmit,
  busy,
  error,
  submitLabel,
  onCancel,
}: {
  initial?: Client
  tagColors: TagColors
  onSubmit: (input: ClientInput) => void
  busy: boolean
  error: string
  submitLabel: string
  /** Shows a quiet „Anuluj” next to the button. */
  onCancel?: () => void
}) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? '')
  const [lastName, setLastName] = useState(initial?.lastName ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [tags, setTags] = useState(initial?.tags.join(', ') ?? '')
  const [price, setPrice] = useState(initial ? String(groszToZloty(initial.defaultPriceGrosz)) : '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [active, setActive] = useState(initial?.active ?? true)
  const [color, setColor] = useState(initial?.color ?? '')

  /** Live preview: what the calendar will actually paint for this student. */
  const typedTags = tags
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
  const painter = coloringTag({ tags: typedTags }, tagColors)
  const shownColor = lessonColor({ color, tags: typedTags }, tagColors)

  const priceNum = price.trim() === '' ? 0 : Number(price.replace(',', '.'))
  const valid = firstName.trim().length > 0 && Number.isFinite(priceNum) && priceNum >= 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        onSubmit({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          tags: typedTags,
          notes,
          defaultPriceGrosz: zlotyToGrosz(priceNum),
          active,
          color,
        })
      }}
      className="grid gap-x-6 gap-y-5 desk:grid-cols-2"
    >
      <Field label="Imię" required>
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="cl-input" />
      </Field>
      <Field label="Nazwisko">
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="cl-input" />
      </Field>
      <Field label="E-mail">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="cl-input" />
      </Field>
      <Field label="Telefon">
        <input value={phone} onChange={(e) => setPhone(e.target.value)} className="cl-input" />
      </Field>
      <Field label="Stawka za lekcję (zł)" hint="Domyślna cena, gdy nie ma pakietu.">
        <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className="cl-input" />
      </Field>
      <Field label="Tagi" hint="Po przecinku: premium, low, speak and roll. Pierwszy kolorowy tag nadaje kolor lekcji.">
        <input value={tags} onChange={(e) => setTags(e.target.value)} className="cl-input" />
      </Field>
      <Field
        group
        label="Kolor w kalendarzu"
        hint={painter ? `Bez wyboru: kolor tagu „${painter}”.` : 'Bez wyboru: domyślny złoty.'}
        className="desk:col-span-2"
      >
        <span className="flex flex-wrap items-center gap-3">
          <ColorDot color={shownColor} size={22} />
          <ColorPicker
            classroom
            value={color}
            onChange={setColor}
            fallback={(painter && tagColors.get(painter)) || DEFAULT_LESSON_COLOR}
            allowInherit
            inheritLabel={painter ? 'Z tagu' : 'Domyślny'}
          />
        </span>
      </Field>

      <Field label="Notatki" className="desk:col-span-2">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="cl-input" />
      </Field>

      {initial && (
        <label className="flex cursor-pointer items-center gap-2.5 desk:col-span-2">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-class-sky-700)]"
          />
          Aktywny (widoczny na listach w kalendarzu)
        </label>
      )}

      {error && (
        <div className="desk:col-span-2">
          <ErrorLine>{error}</ErrorLine>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-5 desk:col-span-2">
        <Button type="submit" disabled={!valid || busy}>
          {busy ? 'Zapisuję…' : submitLabel}
        </Button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="cl-link-quiet">
            Anuluj
          </button>
        )}
      </div>
    </form>
  )
}

/**
 * A labelled field: 12px label above, an optional grey hint below.
 * `group` draws it as a group instead of a <label> — for a row of buttons
 * (a label would hand every click on its text to the first button).
 */
export function Field({
  label,
  hint,
  required,
  group = false,
  className = '',
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  group?: boolean
  className?: string
  children: ReactNode
}) {
  const body = (
    <>
      <span className="mb-1.5 block text-class-label font-semibold text-class-ink-soft">
        {label}
        {required && <span className="text-class-ink-faint"> *</span>}
      </span>
      {children}
      {hint && <span className="mt-1.5 block text-class-label text-class-ink-faint">{hint}</span>}
    </>
  )
  return group ? (
    <div role="group" aria-label={label} className={`flex flex-col ${className}`}>
      {body}
    </div>
  ) : (
    <label className={`flex flex-col ${className}`}>{body}</label>
  )
}
